import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Calendar, Settings, Gauge, Fuel, Users, Zap, Layers, CheckCircle2, Send, Phone, Expand, Star, ShieldCheck,
} from "lucide-react";
import { localize, imgV, hasPromo, termPrice, priceExVat, vatPrice, fmtKc } from "@/data/vehicles";
import { useVatMode } from "@/lib/vatMode";
import InquiryModal from "@/components/InquiryModal";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Lightbox from "@/components/Lightbox";
import TermSwitch from "@/components/TermSwitch";
import CompareTable from "@/components/CompareTable";
import { VehicleCard } from "@/components/VehiclesGallery";
import Seo, { SITE_URL } from "@/components/Seo";
import { useLang } from "@/i18n/LanguageContext";
import { useVehicles } from "@/context/VehiclesContext";

const LOGO_IMG = "/img/n148kmw9_image.webp";
const PHONE = "tel:+420725706235";

// Normalize AI-pasted feature text: split markdown blobs into clean individual bullets,
// strip ** / * markers, and extract an optional bold "Label:" prefix.
const normalizeFeatures = (items, lang) => {
  const out = [];
  (items || []).forEach((raw) => {
    const s = localize(raw, lang);
    if (typeof s !== "string" || !s.trim()) return;
    s.split(/\s*\n+\s*|\s+[*•]\s+|\s+-\s+(?=[A-Za-zÁ-Žá-ž*])/).forEach((part) => {
      const t = part.replace(/\*\*/g, "").replace(/^[\s*•\-]+/, "").trim();
      if (!t) return;
      const m = t.match(/^([^:]{2,40}):\s*(.+)$/);
      out.push(m ? { label: m[1].trim(), text: m[2].trim() } : { label: null, text: t });
    });
  });
  return out;
};

const SpecItem = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-3.5 flex items-center gap-3 hover:border-orange-500/30 transition-colors">
    <div className="w-9 h-9 rounded-xl bg-orange-500/15 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-orange-400" />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="text-zinc-100 text-sm font-semibold truncate">{value}</p>
    </div>
  </div>
);

const PriceCard = ({ vehicle, months, setMonths, onInquiry, t, sticky }) => {
  const price = termPrice(vehicle.priceMonthly, months);
  const vat = useVatMode();
  const vp = vatPrice(price, vat);
  return (
    <div className={`glass rounded-3xl p-5 sm:p-6 border-orange-500/20 ${sticky ? "lg:sticky lg:top-28" : ""}`} data-testid="detail-price-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="text-[11px] uppercase tracking-wider text-zinc-500">{t("detail.monthly")}</p>
        {hasPromo(vehicle) && (
          <span className="bg-orange-500 text-black text-[11px] font-extrabold uppercase tracking-wide px-2.5 py-1 rounded-full" data-testid="detail-promo-badge">
            {vehicle.promoLabel || t("promo.badge")}
          </span>
        )}
      </div>
      {vehicle.priceMonthly == null ? (
        <p className="text-orange-400 font-bold text-3xl">{t("price.onRequest")}</p>
      ) : (
        <>
          {(hasPromo(vehicle) || months > 1) && (
            <p className="text-zinc-500 line-through text-sm" data-testid="detail-price-original">
              {fmtKc(hasPromo(vehicle) ? vehicle.priceOriginal : vehicle.priceMonthly)} Kč {t("price.perMonth")}
            </p>
          )}
          <p className="text-orange-400 font-bold text-4xl sm:text-5xl font-['Cabinet_Grotesk'] leading-none" data-testid="detail-price">
            {fmtKc(vp.main)} Kč <span className="text-base font-semibold text-orange-400/80">{t("price.perMonth")}</span>
          </p>
          <p className="text-zinc-400 text-xs mt-1.5" data-testid="detail-price-exvat">{fmtKc(vp.sub)} Kč {vp.subLabel}</p>
          <p className="text-zinc-500 text-xs mt-2" data-testid="detail-included-line">{t("vehicles.included")}</p>
          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">{t("vehicles.termLabel")}</p>
            <TermSwitch value={months} onChange={setMonths} compact className="flex-wrap" />
          </div>
        </>
      )}

      <ul className="mt-5 space-y-2 text-sm text-zinc-300" data-testid="detail-included">
        <li className="text-[11px] uppercase tracking-wider text-zinc-500">{t("detail.includedTitle")}</li>
        {["detail.inc1", "detail.inc2", "detail.inc3", "detail.inc4"].map((k) => (
          <li key={k} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> {t(k)}</li>
        ))}
      </ul>

      <div className="flex flex-col gap-2 mt-5">
        <button type="button" onClick={onInquiry} className="btn-accent w-full inline-flex items-center justify-center gap-2" data-testid="detail-inquiry-btn">
          <Send className="w-4 h-4" /> {t("detail.inquiryBtn")}
        </button>
        <a href={PHONE} className="btn-outline w-full inline-flex items-center justify-center gap-2" data-testid="detail-call-btn">
          <Phone className="w-4 h-4" /> {t("detail.call")}
        </a>
      </div>
      <p className="text-zinc-500 text-[11px] mt-3 inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> {t("detail.noDeposit")}</p>
    </div>
  );
};

const VehicleDetail = () => {
  const { slug } = useParams();
  const { getBySlug, loading, vehicles } = useVehicles();
  const vehicle = getBySlug(slug);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [months, setMonths] = useState(1);
  const vat = useVatMode();
  const [showInquiry, setShowInquiry] = useState(false);
  const [showSticky, setShowSticky] = useState(false);
  const { lang, t } = useLang();

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 520);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setActiveIndex(0); window.scrollTo(0, 0); }, [slug]);
  // Preload the current photo + its neighbours so switching in the gallery is instant,
  // without firing a heavy burst of every full-size image at once on page load.
  useEffect(() => {
    const imgs = vehicle?.images || [];
    if (imgs.length < 2) return;
    [0, 1, -1].forEach((d) => {
      const im = imgs[(activeIndex + d + imgs.length) % imgs.length];
      if (im?.url) { const i = new Image(); i.src = imgV(im.url, "web"); }
    });
  }, [vehicle, activeIndex]);

  // Up to 3 related cars (same body type first) for the "Podobné vozy" section.
  const similar = useMemo(() => {
    if (!vehicle) return [];
    const others = vehicles.filter((v) => v.slug !== vehicle.slug);
    const sameType = others.filter((v) => localize(v.bodyType, lang) === localize(vehicle.bodyType, lang));
    const rest = others.filter((v) => !sameType.includes(v));
    return [...sameType, ...rest].slice(0, 3);
  }, [vehicles, vehicle, lang]);

  if (!vehicle && loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!vehicle) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center text-center px-4">
        <h1 className="font-['Cabinet_Grotesk'] text-3xl font-bold mb-4">{t("detail.notFoundTitle")}</h1>
        <p className="text-zinc-400 mb-8">{t("detail.notFoundDesc")}</p>
        <Link to="/#vehicles" className="btn-accent" data-testid="back-to-vehicles">{t("detail.back")}</Link>
      </div>
    );
  }

  const images = vehicle.images || [];
  const mainImage = images[activeIndex] || images[0];
  const priceStr = vehicle.priceMonthly != null ? `${fmtKc(vehicle.priceMonthly)} Kč/měsíc` : "Cena na dotaz";
  const seoTitle = `${vehicle.name} – dlouhodobý pronájem | EffectDeal`;
  const seoDesc = `Dlouhodobý pronájem vozu ${vehicle.name} u EffectDeal. ${priceStr}. Pojištění, dálniční známka a servis v ceně. Nezávazně poptejte ještě dnes.`;
  const productJsonLd = {
    "@context": "https://schema.org", "@type": "Product", name: vehicle.name, image: images.map((i) => i.url), description: seoDesc,
    brand: { "@type": "Brand", name: vehicle.name.split(" ")[0] }, category: localize(vehicle.bodyType, lang),
    ...(vehicle.priceMonthly != null && { offers: { "@type": "Offer", priceCurrency: "CZK", price: vehicle.priceMonthly, availability: "https://schema.org/InStock", url: `${SITE_URL}/vozy/${vehicle.slug}` } }),
  };
  const subtitle = [vehicle.year, localize(vehicle.bodyType, lang), vehicle.trim && localize(vehicle.trim, lang)].filter(Boolean).join(" · ");

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="vehicle-detail-page">
      <Seo title={seoTitle} description={seoDesc} path={`/vozy/${vehicle.slug}`} image={images[0]?.url} type="product" jsonLd={productJsonLd} />

      <nav className="fixed top-0 left-0 right-0 z-50 nav-glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <img src={LOGO_IMG} alt="EffectDeal" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
              <span className="font-['Cabinet_Grotesk'] font-bold text-lg sm:text-xl tracking-wide">EFFECTDEAL</span>
            </Link>
            <div className="flex items-center gap-3 sm:gap-4">
              <LanguageSwitcher />
              <Link to="/#vehicles" className="inline-flex text-sm text-zinc-400 hover:text-white transition-colors items-center gap-1.5" data-testid="detail-back-link">
                <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">{t("detail.back")}</span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-20 sm:pt-24 pb-16">
        {/* Hero photo */}
        <section className="max-w-7xl mx-auto px-3 sm:px-6" data-testid="detail-hero">
          <div
            className="relative rounded-3xl overflow-hidden aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9] bg-[#0a0a0a] cursor-zoom-in group"
            onClick={() => images.length && setLightbox(true)}
            data-testid="detail-main-image"
          >
            {mainImage ? (
              <img key={mainImage.url} src={imgV(mainImage.url, "web")} alt={mainImage.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02] animate-fade-in" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-zinc-600">Bez fotografie</div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent pointer-events-none" />

            <div className="absolute top-4 left-4 flex flex-wrap gap-2">
              {vehicle.multipleAvailable && (
                <span className="inline-flex items-center gap-1.5 bg-black/60 backdrop-blur-md border border-white/10 text-emerald-300 text-xs font-medium px-3 py-1.5 rounded-full">
                  <Layers className="w-3.5 h-3.5" /> {t("detail.inStockModel")}
                </span>
              )}
              {hasPromo(vehicle) && (
                <span className="bg-orange-500 text-black text-[11px] font-extrabold uppercase tracking-wide px-3 py-1.5 rounded-full shadow-lg shadow-orange-500/40">{vehicle.promoLabel || t("promo.badge")}</span>
              )}
            </div>

            <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 flex flex-wrap items-end justify-between gap-4">
              <div className="min-w-0">
                <h1 className="font-['Cabinet_Grotesk'] text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white drop-shadow-lg leading-[1.05]">{vehicle.name}</h1>
                {subtitle && <p className="text-zinc-300 mt-2 text-sm sm:text-base drop-shadow">{subtitle}</p>}
              </div>
              {images.length > 0 && (
                <button type="button" onClick={(e) => { e.stopPropagation(); setLightbox(true); }} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white text-white hover:text-black backdrop-blur-md border border-white/20 text-sm font-semibold px-4 py-2.5 rounded-full transition-colors" data-testid="detail-all-photos">
                  <Expand className="w-4 h-4" /> {t("detail.allPhotos")} ({images.length})
                </button>
              )}
            </div>
          </div>

          {images.length > 1 && (
            <div className="flex gap-2 sm:gap-3 mt-3 overflow-x-auto no-scrollbar" data-testid="detail-thumbs">
              {images.map((img, idx) => (
                <button key={img.url} type="button" onMouseEnter={() => setActiveIndex(idx)} onClick={() => setActiveIndex(idx)}
                  className={`relative w-24 h-16 sm:w-32 sm:h-20 flex-shrink-0 rounded-xl overflow-hidden border-2 transition-all ${idx === activeIndex ? "border-orange-500" : "border-transparent opacity-60 hover:opacity-100"}`}
                  data-testid={`detail-thumb-${idx}`} aria-label={img.label}>
                  <img src={imgV(img.url, "thumb")} alt={img.alt} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Content + sticky price */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-8 sm:mt-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8 lg:gap-12 items-start">
            <div className="space-y-8 sm:space-y-10 min-w-0">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="detail-specs">
                {vehicle.year && <SpecItem icon={Calendar} label={t("detail.specYear")} value={vehicle.year} />}
                {vehicle.transmission && <SpecItem icon={Settings} label={t("detail.specTransmission")} value={localize(vehicle.transmission, lang)} />}
                {vehicle.fuel && <SpecItem icon={Fuel} label={t("detail.specFuel")} value={localize(vehicle.fuel, lang)} />}
                {vehicle.powerKw && <SpecItem icon={Zap} label={t("detail.specPower")} value={`${vehicle.powerKw} kW`} />}
                {vehicle.seats && <SpecItem icon={Users} label={t("detail.specSeats")} value={vehicle.seats} />}
                {vehicle.mileage && <SpecItem icon={Gauge} label={t("detail.specMileage")} value={localize(vehicle.mileage, lang)} />}
              </div>

              {vehicle.description && (
                <p className="text-zinc-300 text-base sm:text-lg leading-relaxed" data-testid="detail-description">{localize(vehicle.description, lang)}</p>
              )}

              <div className="lg:hidden">
                <PriceCard vehicle={vehicle} months={months} setMonths={setMonths} onInquiry={() => setShowInquiry(true)} t={t} />
              </div>

              <CompareTable vehicle={vehicle} />

              {vehicle.featureGroups?.length > 0 && (
                <div>
                  <h2 className="font-['Cabinet_Grotesk'] text-2xl sm:text-3xl font-bold mb-5">{t("detail.equipment")}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {vehicle.featureGroups.map((group, gi) => (
                      <div key={gi} className="rounded-2xl bg-white/[0.03] border border-white/[0.06] p-5" data-testid={`feature-group-${gi}`}>
                        <h3 className="font-['Cabinet_Grotesk'] text-base font-bold mb-3 text-orange-200">{localize(group.title, lang)}</h3>
                        <ul className="space-y-2">
                          {normalizeFeatures(group.items, lang).map((item, ii) => (
                            <li key={ii} className="flex items-start gap-2.5 text-zinc-300 text-sm">
                              <CheckCircle2 className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                              <span>{item.label && <strong className="text-white font-semibold">{item.label}: </strong>}{item.text}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="rounded-3xl bg-gradient-to-br from-orange-500/15 to-transparent border border-orange-500/20 p-6 sm:p-10 text-center">
                <p className="inline-flex items-center gap-1.5 text-xs text-zinc-300 mb-3"><Star className="w-3.5 h-3.5 text-orange-400 fill-orange-400" /> {t("vehicles.googleTrust")}</p>
                <h3 className="font-['Cabinet_Grotesk'] text-xl sm:text-2xl font-bold mb-2">{t("detail.interestedPre")} {vehicle.name}?</h3>
                <p className="text-zinc-400 mb-6 text-sm sm:text-base">{t("detail.interestedDesc")}</p>
                <button type="button" onClick={() => setShowInquiry(true)} className="btn-accent inline-flex items-center gap-2" data-testid="detail-bottom-cta">
                  <Send className="w-4 h-4" /> {t("detail.bottomCta")}
                </button>
              </div>
            </div>

            <aside className="hidden lg:block">
              <PriceCard vehicle={vehicle} months={months} setMonths={setMonths} onInquiry={() => setShowInquiry(true)} t={t} sticky />
            </aside>
          </div>
        </section>
      </main>

      {lightbox && <Lightbox images={images} index={activeIndex} onChange={setActiveIndex} onClose={() => setLightbox(false)} />}

      <InquiryModal vehicleName={`${vehicle.name}${months > 1 ? ` · ${months} ${t("vehicles.monthsN")}` : ""}`} open={showInquiry} onClose={() => setShowInquiry(false)} />

      {/* Sticky bottom bar on mobile/tablet */}
      <div
        className={`lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0d0d0d]/95 backdrop-blur-md border-t border-white/10 transition-transform duration-300 ${showSticky && !showInquiry && !lightbox ? "translate-y-0" : "translate-y-full"}`}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="detail-sticky-bar"
      >
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-white font-['Cabinet_Grotesk'] font-bold text-sm truncate">{vehicle.name}</p>
            <p className="text-orange-400 font-bold text-sm whitespace-nowrap">
              {vehicle.priceMonthly == null ? t("price.onRequest") : `${fmtKc(vatPrice(termPrice(vehicle.priceMonthly, months), vat).main)} Kč ${t("price.perMonth")}`}
            </p>
            {vehicle.priceMonthly != null && (
              <p className="text-zinc-500 text-[10px] whitespace-nowrap">{fmtKc(vatPrice(termPrice(vehicle.priceMonthly, months), vat).sub)} Kč {vatPrice(termPrice(vehicle.priceMonthly, months), vat).subLabel}</p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a href={PHONE} className="btn-outline inline-flex items-center justify-center w-11 h-11 !p-0 rounded-full" aria-label={t("detail.call")} data-testid="sticky-call-btn"><Phone className="w-4 h-4" /></a>
            <button type="button" onClick={() => setShowInquiry(true)} className="btn-accent inline-flex items-center gap-2 text-sm px-5 py-2.5" data-testid="sticky-inquiry-btn">
              <Send className="w-4 h-4" /> {t("detail.inquiryBtn")}
            </button>
          </div>
        </div>
      </div>

      <footer className="py-8 px-4 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          {similar.length > 0 && (
            <section className="pb-10 mb-8 border-b border-white/5" data-testid="detail-similar">
              <div className="flex items-end justify-between gap-3 mb-5">
                <h2 className="font-['Cabinet_Grotesk'] text-xl sm:text-2xl font-bold">{t("detail.similarTitle")}</h2>
                <Link to="/#vehicles" className="text-sm text-orange-400 hover:text-orange-300 inline-flex items-center gap-1.5 whitespace-nowrap" data-testid="detail-similar-all">
                  {t("detail.similarAll")} <ArrowLeft className="w-4 h-4 rotate-180" />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                {similar.map((v) => <VehicleCard key={v.slug} vehicle={v} months={months} />)}
              </div>
            </section>
          )}
          <p className="text-zinc-600 text-xs text-center">© {new Date().getFullYear()} EffectDeal · IČO: 24952923. {t("common.rights")}</p>
        </div>
      </footer>
    </div>
  );
};

export default VehicleDetail;
